import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link as RouterLink, useNavigate, useSearchParams } from "react-router-dom";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Fab from "@mui/material/Fab";
import FormControlLabel from "@mui/material/FormControlLabel";
import IconButton from "@mui/material/IconButton";
import InputAdornment from "@mui/material/InputAdornment";
import Stack from "@mui/material/Stack";
import Switch from "@mui/material/Switch";
import Tab from "@mui/material/Tab";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import TableSortLabel from "@mui/material/TableSortLabel";
import Tabs from "@mui/material/Tabs";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import CircularProgress from "@mui/material/CircularProgress";
import Paper from "@mui/material/Paper";
import AddIcon from "@mui/icons-material/Add";
import ClearIcon from "@mui/icons-material/Clear";
import DeleteIcon from "@mui/icons-material/Delete";
import SearchIcon from "@mui/icons-material/Search";
import {
  deleteUserWine,
  listWineCollection,
  listWineWantlist,
  updateUserWine,
} from "../utils/apiClient";
import useDocumentTitle from "../hooks/useDocumentTitle";

const COLOR_LABELS = {
  red: "Rød",
  white: "Hvid",
  rose: "Rosé",
  orange: "Orange",
  other: "Andet",
};

const SORTABLE_COLUMNS = [
  { id: "producer", label: "Producent" },
  { id: "wine", label: "Vin" },
  { id: "vintage", label: "Årgang" },
  { id: "color", label: "Farve" },
  { id: "country", label: "Land" },
  { id: "region", label: "Region" },
  { id: "grapes", label: "Druer", sortable: false },
  { id: "quantity", label: "Antal", collectionOnly: true },
];

function vintageYear(item) {
  const v = item?.vintage || {};
  if (v.is_non_vintage) return "NV";
  return v.vintage_year || "—";
}

function parseQty(raw) {
  const n = Number(raw);
  if (!Number.isFinite(n)) return 0;
  return Math.max(0, Math.trunc(n));
}

function WineManager() {
  useDocumentTitle("Vin manager");
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const tab = searchParams.get("tab") === "wantlist" ? "wantlist" : "collection";
  const q = searchParams.get("q") || "";
  const sort = searchParams.get("sort") || "producer";
  const sortDir = searchParams.get("sort_dir") === "desc" ? "desc" : "asc";
  const showDrunk = searchParams.get("drunk") === "1";

  const [searchInput, setSearchInput] = useState(q);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    setSearchInput(q);
  }, [q]);

  const queryParams = useMemo(() => {
    const params = { sort, sort_dir: sortDir };
    if (q) params.q = q;
    if (tab === "collection" && showDrunk) params.include_zero = true;
    return params;
  }, [q, sort, sortDir, tab, showDrunk]);

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const data =
        tab === "wantlist"
          ? await listWineWantlist(queryParams)
          : await listWineCollection(queryParams);
      setItems(Array.isArray(data) ? data : []);
    } catch (err) {
      const detail = err.response?.data?.detail;
      setError(typeof detail === "string" ? detail : err.message || "Kunne ikke hente vinkælder");
    } finally {
      setLoading(false);
    }
  }, [tab, queryParams]);

  useEffect(() => {
    load();
  }, [load]);

  const setTab = (next) => {
    const params = new URLSearchParams(searchParams);
    if (next === "wantlist") params.set("tab", "wantlist");
    else params.delete("tab");
    setSearchParams(params, { replace: true });
  };

  const updateParams = (updates) => {
    const next = new URLSearchParams(searchParams);
    Object.entries(updates).forEach(([key, value]) => {
      if (value == null || value === "") next.delete(key);
      else next.set(key, value);
    });
    setSearchParams(next, { replace: true });
  };

  const handleSearchSubmit = (event) => {
    event.preventDefault();
    updateParams({ q: searchInput.trim() });
  };

  const handleSort = (columnId) => {
    if (columnId === sort) {
      updateParams({ sort_dir: sortDir === "asc" ? "desc" : "asc" });
      return;
    }
    updateParams({ sort: columnId, sort_dir: "asc" });
  };

  const removeItem = async (item) => {
    if (!window.confirm("Fjern denne vin?")) return;
    try {
      await deleteUserWine(item.user_wine_uuid);
      await load();
    } catch (err) {
      const detail = err.response?.data?.detail;
      setError(typeof detail === "string" ? detail : "Kunne ikke slette");
    }
  };

  const changeQty = async (item, quantity) => {
    const next = parseQty(quantity);
    try {
      await updateUserWine(item.user_wine_uuid, { quantity: next });
      // Qty 0 is hidden by default — turn on drunk view so the row stays visible
      if (next === 0 && tab === "collection" && !showDrunk) {
        updateParams({ drunk: "1" });
        return;
      }
      await load();
    } catch (err) {
      const detail = err.response?.data?.detail;
      setError(typeof detail === "string" ? detail : "Kunne ikke opdatere antal");
    }
  };

  const visibleColumns = SORTABLE_COLUMNS.filter(
    (col) => !(col.collectionOnly && tab !== "collection")
  );

  return (
    <Box sx={{ position: "relative", py: { xs: 1, sm: 2 } }}>
      <Typography variant="h5" sx={{ fontWeight: 700, letterSpacing: "-0.02em", mb: 1 }}>
        Vin manager
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Samling og ønskeliste. Søg via GrapeMinds eller tilføj manuelt.
      </Typography>

      <Tabs
        value={tab}
        onChange={(_, value) => setTab(value)}
        sx={{ mb: 2, borderBottom: 1, borderColor: "divider" }}
      >
        <Tab value="collection" label="Samling" />
        <Tab value="wantlist" label="Ønskeliste" />
      </Tabs>

      <Stack
        component="form"
        onSubmit={handleSearchSubmit}
        direction={{ xs: "column", sm: "row" }}
        spacing={1.5}
        alignItems={{ sm: "center" }}
        sx={{ mb: 2 }}
      >
        <TextField
          size="small"
          fullWidth
          value={searchInput}
          onChange={(e) => setSearchInput(e.target.value)}
          placeholder="Søg producent, vin, noter…"
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" />
              </InputAdornment>
            ),
            endAdornment: searchInput ? (
              <InputAdornment position="end">
                <IconButton
                  size="small"
                  aria-label="Ryd"
                  onClick={() => {
                    setSearchInput("");
                    updateParams({ q: "" });
                  }}
                >
                  <ClearIcon fontSize="small" />
                </IconButton>
              </InputAdornment>
            ) : null,
          }}
        />
        <Button type="submit" variant="outlined">
          Søg
        </Button>
        {tab === "collection" ? (
          <FormControlLabel
            sx={{ ml: { sm: 0.5 }, whiteSpace: "nowrap" }}
            control={
              <Switch
                size="small"
                checked={showDrunk}
                onChange={(e) => updateParams({ drunk: e.target.checked ? "1" : "" })}
              />
            }
            label="Vis drukkede vine"
          />
        ) : null}
      </Stack>

      {loading ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
          <CircularProgress size={28} />
        </Box>
      ) : error ? (
        <Typography color="error">{error}</Typography>
      ) : items.length === 0 ? (
        <Typography color="text.secondary">
          {tab === "wantlist" ? "Ønskelisten er tom." : "Samlingen er tom."}
        </Typography>
      ) : (
        <TableContainer
          component={Paper}
          variant="outlined"
          sx={{ maxWidth: "100%", overflowX: "auto", mb: 10 }}
        >
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                {visibleColumns.map((col) => (
                  <TableCell
                    key={col.id}
                    align={col.id === "quantity" ? "right" : "left"}
                    sortDirection={sort === col.id ? sortDir : false}
                  >
                    {col.sortable === false ? (
                      col.label
                    ) : (
                      <TableSortLabel
                        active={sort === col.id}
                        direction={sort === col.id ? sortDir : "asc"}
                        onClick={() => handleSort(col.id)}
                      >
                        {col.label}
                      </TableSortLabel>
                    )}
                  </TableCell>
                ))}
                <TableCell align="right" sx={{ width: 48, px: 0.5, fontSize: "0.75rem" }}>
                  Slet
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {items.map((item) => {
                const v = item.vintage || {};
                const detailTo = `/tools/wine-manager/bottle/${item.user_wine_uuid}`;
                const drunk = tab === "collection" && Number(item.quantity) === 0;
                const italicSx = drunk ? { fontStyle: "italic" } : undefined;
                return (
                  <TableRow
                    key={item.user_wine_uuid}
                    hover
                    sx={{ cursor: "pointer", ...(drunk ? { opacity: 0.85 } : null) }}
                    onClick={() => navigate(detailTo)}
                  >
                    <TableCell sx={{ whiteSpace: "nowrap", ...italicSx }}>
                      {v.producer_name || "—"}
                    </TableCell>
                    <TableCell sx={{ fontWeight: 600, ...italicSx }}>
                      {v.wine_name || "—"}
                    </TableCell>
                    <TableCell sx={italicSx}>{vintageYear(item)}</TableCell>
                    <TableCell sx={italicSx}>
                      {COLOR_LABELS[v.color_name] || v.color_name || "—"}
                    </TableCell>
                    <TableCell sx={{ whiteSpace: "nowrap", ...italicSx }}>
                      {v.country_name || "—"}
                    </TableCell>
                    <TableCell sx={italicSx}>{v.geography_name || "—"}</TableCell>
                    <TableCell sx={{ minWidth: 100, maxWidth: 160, ...italicSx }}>
                      {(v.grapes || []).length ? v.grapes.join(", ") : "—"}
                    </TableCell>
                    {tab === "collection" ? (
                      <TableCell
                        align="right"
                        sx={{ width: 64, px: 0.5, ...italicSx }}
                        onClick={(e) => e.stopPropagation()}
                      >
                        <TextField
                          type="number"
                          size="small"
                          value={item.quantity}
                          onChange={(e) => changeQty(item, e.target.value)}
                          inputProps={{
                            min: 0,
                            "aria-label": "Antal",
                          }}
                          sx={{
                            width: 52,
                            "& input": {
                              textAlign: "right",
                              py: 0.5,
                              px: 0.5,
                              fontStyle: drunk ? "italic" : "normal",
                            },
                          }}
                        />
                      </TableCell>
                    ) : null}
                    <TableCell
                      align="right"
                      sx={{ width: 48, px: 0.5 }}
                      onClick={(e) => e.stopPropagation()}
                    >
                      <IconButton aria-label="Slet" size="small" onClick={() => removeItem(item)}>
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <Fab
        color="primary"
        aria-label="Tilføj vin"
        component={RouterLink}
        to={`/tools/wine-manager/add?wantlist=${tab === "wantlist" ? "1" : "0"}`}
        sx={{
          position: "fixed",
          right: { xs: 16, sm: 24 },
          bottom: { xs: 16, sm: 24 },
        }}
      >
        <AddIcon />
      </Fab>
    </Box>
  );
}

export default WineManager;
